import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";
import "../styles/MissionaryManagement.css";

interface Missionary {
  id: number;
  number: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  region: string | null;
  province: string | null;
  target_upg: string | null;
  partner_org: string | null;
  supported_by: string | null;
  project_name: string | null;
  role: string | null;
  reports_to: string | null;
  date_began: string | null;
  is_active: boolean;
  notes: string | null;
  email: string | null;
  phone: string | null;
}

interface FormData {
  first_name: string;
  last_name: string;
  region: string;
  province: string;
  target_upg: string;
  partner_org: string;
  supported_by: string;
  project_name: string;
  role: string;
  reports_to: string;
  date_began: string;
  is_active: boolean;
  notes: string;
  email: string;
  phone: string;
}

const EMPTY_FORM: FormData = {
  first_name: "", last_name: "", region: "", province: "",
  target_upg: "", partner_org: "", supported_by: "", project_name: "",
  role: "", reports_to: "", date_began: "", is_active: true, notes: "",
  email: "", phone: "",
};

interface TagItem { id: number; name: string; }
interface MissionaryTags { projects: TagItem[]; peopleGroups: TagItem[]; villages: TagItem[]; }

function MissionaryManagement({ user }: { user: User }): React.ReactElement {
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMissionary, setEditingMissionary] = useState<Missionary | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("number");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [allProjects, setAllProjects] = useState<TagItem[]>([]);
  const [allPeopleGroups, setAllPeopleGroups] = useState<TagItem[]>([]);
  const [allVillages, setAllVillages] = useState<TagItem[]>([]);
  const [mTags, setMTags] = useState<MissionaryTags>({ projects: [], peopleGroups: [], villages: [] });

  const isAdmin = ["admin", "superadmin", "system_admin"].includes(user.role);

  useEffect(() => {
    fetchMissionaries();
    fetchAllTags();
  }, []);

  const fetchMissionaries = async () => {
    try {
      const url = isAdmin ? "/api/missionaries" : `/api/users/${user.id}/missionaries`;
      const res = await axios.get(url);
      setMissionaries(res.data.missionaries || []);
    } catch { setErr("Failed to load missionaries"); }
    finally { setLoading(false); }
  };

  const fetchAllTags = async () => {
    try {
      const [p, pg, v] = await Promise.all([
        axios.get("/api/tags/projects"),
        axios.get("/api/tags/people-groups"),
        axios.get("/api/tags/villages"),
      ]);
      setAllProjects(p.data.projects || []);
      setAllPeopleGroups(pg.data.peopleGroups || []);
      setAllVillages(v.data.villages || []);
    } catch { /* non-fatal */ }
  };

  const fetchMissionaryTags = async (id: number) => {
    try {
      const res = await axios.get(`/api/tags/missionaries/${id}/tags`);
      setMTags(res.data);
    } catch { setMTags({ projects: [], peopleGroups: [], villages: [] }); }
  };

  const toggleTag = async (mId: number, type: "projects" | "people-groups" | "villages", itemId: number, has: boolean) => {
    if (has) await axios.delete(`/api/tags/missionaries/${mId}/${type}/${itemId}`);
    else await axios.post(`/api/tags/missionaries/${mId}/${type}/${itemId}`);
    await fetchMissionaryTags(mId);
  };

  const handleEdit = (m: Missionary) => {
    setEditingMissionary(m);
    fetchMissionaryTags(m.id);
    setFormData({
      first_name: m.first_name || "",
      last_name: m.last_name || "",
      region: m.region || "",
      province: m.province || "",
      target_upg: m.target_upg || "",
      partner_org: m.partner_org || "",
      supported_by: m.supported_by || "",
      project_name: m.project_name || "",
      role: m.role || "",
      reports_to: m.reports_to || "",
      date_began: m.date_began ? m.date_began.split("T")[0] : "",
      is_active: m.is_active !== false,
      notes: m.notes || "",
      email: m.email || "",
      phone: m.phone || "",
    });
    setErr(""); setMsg("");
  };

  const handleCancel = () => {
    setEditingMissionary(null);
    setFormData(EMPTY_FORM);
    setErr(""); setMsg("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(""); setMsg("");
    try {
      await axios.put(`/api/missionaries/${editingMissionary?.id}`, formData);
      setMsg("Saved.");
      fetchMissionaries();
      setTimeout(handleCancel, 1500);
    } catch (e: any) {
      setErr(e.response?.data?.error || "Failed to save");
    }
  };

  const handleToggleActive = async (m: Missionary) => {
    const next = m.is_active === false ? "active" : "inactive";
    if (!window.confirm(`Mark ${m.name || m.number} as ${next}?`)) return;
    try {
      await axios.patch(`/api/missionaries/${m.id}/toggle-active`);
      fetchMissionaries();
    } catch { setErr("Failed to update status"); }
  };

  const handleDelete = async (m: Missionary) => {
    if (!window.confirm(`Delete ${m.name || m.number}? This can be undone by an administrator.`)) return;
    try {
      await axios.delete(`/api/missionaries/${m.id}`);
      fetchMissionaries();
    } catch { setErr("Failed to delete"); }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = missionaries.filter(m => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      m.number?.toLowerCase().includes(q) ||
      m.name?.toLowerCase().includes(q) ||
      m.first_name?.toLowerCase().includes(q) ||
      m.last_name?.toLowerCase().includes(q) ||
      m.role?.toLowerCase().includes(q) ||
      m.province?.toLowerCase().includes(q) ||
      m.region?.toLowerCase().includes(q) ||
      m.target_upg?.toLowerCase().includes(q) ||
      m.partner_org?.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = ((a as any)[sortKey] || "").toString().toLowerCase();
    const bv = ((b as any)[sortKey] || "").toString().toLowerCase();
    const cmp = av.localeCompare(bv, undefined, { numeric: true });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const SortTh = ({ col, label }: { col: string; label: string }) => (
    <th onClick={() => handleSort(col)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {label} <span style={{ color: sortKey === col ? "inherit" : "#cbd5e0", fontSize: "0.8em" }}>
        {sortKey === col ? (sortDir === "asc" ? "▲" : "▼") : "▼"}
      </span>
    </th>
  );

  const F = ({ label, name, placeholder, type = "text" }: { label: string; name: keyof FormData; placeholder?: string; type?: string }) => (
    <div className="missionaries-form-group">
      <label>{label}</label>
      <input type={type} name={name as string} value={formData[name] as string}
        onChange={handleChange} placeholder={placeholder} />
    </div>
  );

  if (loading) return <div className="missionaries-loading">Loading missionaries...</div>;

  return (
    <div className="missionaries-container">
      <h2>{isAdmin ? "Missionary Management" : "My Missionaries"}</h2>

      <div className="missionaries-stats">
        <div className="missionaries-stat-item"><span className="missionaries-stat-label">Total:</span><span className="missionaries-stat-value total">{missionaries.length}</span></div>
        <div className="missionaries-stat-item"><span className="missionaries-stat-label">Active:</span><span className="missionaries-stat-value active">{missionaries.filter(m => m.is_active !== false).length}</span></div>
        {isAdmin && <><div className="missionaries-stat-item"><span className="missionaries-stat-label">Inactive:</span><span className="missionaries-stat-value inactive">{missionaries.filter(m => m.is_active === false).length}</span></div>
        <div className="missionaries-stat-item"><span className="missionaries-stat-label">Showing:</span><span className="missionaries-stat-value">{filtered.length}</span></div></>}
      </div>

      {msg && !editingMissionary && <div className="missionaries-success-message" style={{ marginBottom: 16 }}>{msg}</div>}
      {err && !editingMissionary && <div className="missionaries-error-message" style={{ marginBottom: 16 }}>{err}</div>}

      <div className="missionaries-filters-section">
        <div className="missionaries-search-group">
          <label>Search</label>
          <input type="text" placeholder="Name, number, role, province, UPG…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="missionaries-search-input" />
        </div>
      </div>

      {/* Edit Modal */}
      {isAdmin && editingMissionary && (
        <div className="missionaries-modal-overlay" onClick={handleCancel}>
          <div className="missionaries-modal-content" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div className="missionaries-modal-header">
              <h3>Edit Worker: {editingMissionary.number}</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="missionaries-modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>

                <div style={{ fontWeight: 600, color: "#15403a", marginBottom: 8, marginTop: 4, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Identity</div>
                <div className="missionaries-form-row">
                  <F label="First Name" name="first_name" placeholder="e.g. Shar" />
                  <F label="Last Name" name="last_name" placeholder="e.g. Habib" />
                </div>

                <div style={{ fontWeight: 600, color: "#15403a", marginBottom: 8, marginTop: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Role</div>
                <div className="missionaries-form-row">
                  <div className="missionaries-form-group">
                    <label>Role</label>
                    <select name="role" value={formData.role} onChange={handleChange}>
                      <option value="">— select —</option>
                      <option>Church Planter</option>
                      <option>Regional Coordinator</option>
                      <option>Field Supervisor</option>
                      <option>Support Worker</option>
                    </select>
                  </div>
                  <F label="Reports To" name="reports_to" placeholder="e.g. John Kille" />
                </div>

                <div style={{ fontWeight: 600, color: "#15403a", marginBottom: 8, marginTop: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Location</div>
                <div className="missionaries-form-row">
                  <F label="Multi-Country Region" name="region" placeholder="e.g. East Africa" />
                  <F label="Province" name="province" placeholder="e.g. South Omo" />
                </div>

                <div style={{ fontWeight: 600, color: "#15403a", marginBottom: 8, marginTop: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Alignment</div>
                <div className="missionaries-form-row">
                  <F label="Target UPG" name="target_upg" placeholder="e.g. Toposa" />
                  <F label="Partner Organization" name="partner_org" placeholder="e.g. Water of Life" />
                </div>
                <div className="missionaries-form-row">
                  <F label="Supported By" name="supported_by" placeholder="e.g. Bob Smith" />
                  <F label="Project Name" name="project_name" placeholder="e.g. Jesus for the Triangle" />
                </div>

                <div style={{ fontWeight: 600, color: "#15403a", marginBottom: 8, marginTop: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Dates & Status</div>
                <div className="missionaries-form-row">
                  <F label="Date Began" name="date_began" type="date" />
                  <div className="missionaries-form-group">
                    <label>Status</label>
                    <select name="is_active" value={formData.is_active ? "true" : "false"}
                      onChange={e => setFormData(p => ({ ...p, is_active: e.target.value === "true" }))}>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div style={{ fontWeight: 600, color: "#15403a", marginBottom: 8, marginTop: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Notes</div>
                <div className="missionaries-form-group">
                  <textarea name="notes" value={formData.notes} onChange={handleChange}
                    placeholder="e.g. Psalm 23" rows={3}
                    style={{ width: "100%", padding: "7px 10px", border: "1px solid #cbd5d2", borderRadius: 5, fontSize: 14, resize: "vertical" }} />
                </div>

                <details style={{ marginTop: 16 }}>
                  <summary style={{ fontSize: 13, color: "#718096", cursor: "pointer", userSelect: "none" }}>Contact info (email / phone)</summary>
                  <div className="missionaries-form-row" style={{ marginTop: 10 }}>
                    <F label="Email" name="email" type="email" placeholder="email@example.com" />
                    <F label="Phone" name="phone" placeholder="+1 555 000 0000" />
                  </div>
                </details>

                {err && <div className="missionaries-error-message" style={{ marginTop: 12 }}>{err}</div>}
                {msg && <div className="missionaries-success-message" style={{ marginTop: 12 }}>{msg}</div>}
              </div>

              <div className="missionaries-modal-footer">
                <button type="button" onClick={handleCancel} className="missionaries-cancel-btn">Cancel</button>
                <button type="submit" className="missionaries-submit-btn">Save Changes</button>
              </div>
            </form>

            {/* Tags panel */}
            <div style={{ borderTop: "1px solid #e2e8f0", padding: "16px 24px 20px" }}>
              <p style={{ margin: "0 0 12px", fontWeight: 600, color: "#2d3748", fontSize: 14 }}>Tags</p>
              {([
                { label: "Projects", all: allProjects, assigned: mTags.projects, type: "projects" as const },
                { label: "People Groups", all: allPeopleGroups, assigned: mTags.peopleGroups, type: "people-groups" as const },
                { label: "Villages", all: allVillages, assigned: mTags.villages, type: "villages" as const },
              ]).map(({ label, all, assigned, type }) => (
                <div key={type} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#718096", marginBottom: 5 }}>{label}</div>
                  {all.length === 0 ? (
                    <span style={{ fontSize: 12, color: "#a0aec0" }}>None set up yet — use Reference Lists in the menu.</span>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {all.map(item => {
                        const has = assigned.some(a => a.id === item.id);
                        return (
                          <span key={item.id} onClick={() => toggleTag(editingMissionary!.id, type, item.id, has)}
                            style={{ cursor: "pointer", padding: "3px 10px", borderRadius: 12, fontSize: 12,
                              background: has ? "#2d7a5e" : "#eef2f1", color: has ? "#fff" : "#444",
                              border: `1px solid ${has ? "#2d7a5e" : "#cbd5d2"}`, userSelect: "none" }}>
                            {item.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="missionaries-table-container">
        <table className="missionaries-table">
          <thead>
            <tr>
              <SortTh col="number" label="Code" />
              <SortTh col="name" label="Name" />
              <SortTh col="role" label="Role" />
              <SortTh col="province" label="Province" />
              <SortTh col="target_upg" label="Target UPG" />
              <SortTh col="partner_org" label="Partner Org" />
              <th>Status</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className="no-data">
                {searchTerm ? "No missionaries match your search" : "No missionaries registered"}
              </td></tr>
            ) : sorted.map(m => (
              <tr key={m.id} className={m.is_active === false ? "inactive-row" : ""}>
                <td><strong>{m.number}</strong></td>
                <td>
                  {m.first_name || m.last_name
                    ? `${m.first_name || ""} ${m.last_name || ""}`.trim()
                    : m.name}
                </td>
                <td style={{ color: "#718096", fontSize: 13 }}>{m.role || "—"}</td>
                <td style={{ color: "#718096", fontSize: 13 }}>{m.province || "—"}</td>
                <td style={{ color: "#718096", fontSize: 13 }}>{m.target_upg || "—"}</td>
                <td style={{ color: "#718096", fontSize: 13 }}>{m.partner_org || "—"}</td>
                <td>
                  <span className={`status-badge ${m.is_active === false ? "inactive" : "active"}`}>
                    {m.is_active === false ? "Inactive" : "Active"}
                  </span>
                </td>
                {isAdmin && (
                  <td>
                    <div className="missionaries-action-buttons">
                      <button onClick={() => handleEdit(m)} className="missionaries-edit-btn">Edit</button>
                      <button onClick={() => handleToggleActive(m)}
                        className={m.is_active === false ? "missionaries-activate-btn" : "missionaries-deactivate-btn"}>
                        {m.is_active === false ? "Activate" : "Deactivate"}
                      </button>
                      <button onClick={() => handleDelete(m)} className="missionaries-delete-btn">Delete</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MissionaryManagement;
